import { format } from "date-fns";
import { ClientBookingData, ClientBookingGuest, ClientBookingStop, CreateClientBookingPayload } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { GuestBookingObject, GuestBookingStop, GuestQuoteBody } from "@/redux/apis/guest/types";
import { BookingPassenger, ClientBookingState } from "./types";

/**
 * How the web client identifies itself on quote/create — the values legacy web
 * bookings are stored with (BookingService defaults booking_device='browser',
 * booking_platform='website'). Note the V2 /cust/booking path force-overwrites
 * booking_platform to 'api' server-side; only booking_device passes through.
 */
export const BOOKING_DEVICE = "browser";
export const BOOKING_PLATFORM = "website";

/** Mobile JobRate.JOURNEY_RATE — the default when no transfer type is chosen. */
const DEFAULT_JOB_RATE = "journey_rate";

/** The three billing shapes a transfer type maps to. `fixed_hourly_rate`/`fixed_daily_rate` fold in. */
export type TransferRateKind = "journey" | "hourly" | "daily";

/**
 * Classify a transfer type's rate (its `type` field, e.g. "journey_rate" / "hourly_rate" /
 * "daily_rate" / "fixed_hourly_rate"). Hourly and daily rates require a `booked_for_duration`; journey
 * (and plain fixed point-to-point) don't. Matches the mobile mapping (transfer_type.type → job_rate).
 */
export const transferRateKind = (type: string | null | undefined): TransferRateKind => {
  const t = (type ?? "").toLowerCase();
  if (t.includes("daily")) return "daily";
  if (t.includes("hourly")) return "hourly";
  return "journey";
};

/**
 * True when the transfer type carries a manually-set price (the `fixed_*` job rates). These are
 * admin-only in config, so a self-service client never selects one; they surface only under operator
 * OBO. The legacy sheet reveals a "Set fixed rate" input for exactly these and sends `fixed_price`
 * (BookingManager/js/booking.js `fixedPriceBookingToggle` / `isFixedPrice*Booking`). Matches on the
 * rate string so it works off either the fresh transfer type's `type` or the hydrated `jobRate`.
 */
export const isFixedRate = (type: string | null | undefined): boolean => (type ?? "").toLowerCase().startsWith("fixed");

/** True once a fixed-rate booking has a usable operator-entered price (> 0). */
const hasFixedPrice = (state: ClientBookingState): boolean => !!state.fixedPrice && state.fixedPrice > 0;

/** The rate string driving fixed/duration decisions — the committed `jobRate`, or the live transfer type before Proceed. */
const effectiveRate = (state: ClientBookingState): string | null | undefined => state.jobRate ?? state.transferType?.type;

/** True when the chosen transfer type bills by time and therefore needs a duration. */
export const durationRequired = (state: ClientBookingState): boolean => transferRateKind(state.transferType?.type) !== "journey";

/** Draft POI → the `Stop` waypoint shape mobile sends (app-customer BookingForm.ts). */
/**
 * The chosen passenger → the `vehicles[0].guest` contact the create API reads. Only built for a
 * favourite / new guest ("self" books as the account holder, so no guest is sent — the backend then
 * records booked_for=ACCOUNT_HOLDER). Mirrors mobile's `vehicles[0].guest = form.guest`.
 */
const toBookingGuest = (passenger: BookingPassenger): ClientBookingGuest => ({
  title: passenger.title ?? "",
  first_name: passenger.firstName,
  last_name: passenger.lastName,
  email: passenger.email,
  phone: passenger.phone,
});

/**
 * The chosen extras as a flat list of line-item ids, one entry per unit. Quantity is expressed by
 * REPEATING an id: the backend counts duplicate `line-item` entries (JobAnalyzer's OptionFactory
 * tallies them, ExtraOptionAbstract then prices `unit price x count` and stamps the quantity), so
 * two baby capsules go out as the same id twice. A missing or zero quantity still sends one.
 */
const toExtraLineItems = (state: ClientBookingState): string[] =>
  (state.extras ?? []).flatMap((extra) => Array.from({ length: Math.max(1, extra.quantity ?? 1) }, () => extra.extra_id));

/**
 * Additional passengers (Passenger 2..N) for the job_passenger list. Row 0 is the main passenger
 * (already sent as the account holder / vehicles[0].guest), so only rows 1+ with a non-empty name
 * are extras. Returns [] when there are none.
 */
const toExtraPassengers = (state: ClientBookingState): { name: string; phone?: string }[] =>
  (state.passengers ?? [])
    .filter((_, index) => index > 0)
    .filter((passenger) => passenger.name.trim())
    .map((passenger) => ({ name: passenger.name.trim(), ...(passenger.phone?.trim() ? { phone: passenger.phone.trim() } : {}) }));

/**
 * The POI identity fields both stop shapes carry, following the rider app's address payload
 * (app-customer src/helpers/addressHelper.ts). Mobile also sends `rm_note`, which the backend does
 * store (BookingManager Entity/Location::setRmNote) — web's `PointsOfInterest` has no such field to
 * send, so a favourite's note still never reaches the job.
 * Defaulted rather than omitted so a stop's shape doesn't vary with how the POI was picked.
 */
const toStopIdentity = (poi: PointsOfInterest) => ({
  rm_label: poi.rm_label || "",
  rm_type: poi.rm_type || "",
  rm_tags: poi.rm_tags || [],
  rm_exclusions: poi.rm_exclusions || [],
});

const toBookingStop = (poi: PointsOfInterest): ClientBookingStop => ({
  address: poi.address,
  // Web POIs carry the full address in `address`; mobile's Stop splits the two.
  formatted_address: poi.address,
  lat: poi.lat,
  lng: poi.lng,
  city: poi.city,
  postcode: poi.postcode,
  state: poi.state,
  state_code: poi.state_code,
  country: poi.country,
  // The codes decide the pricing country server-side; a missing country_code is read as AU (DEV-1612).
  country_code: poi.country_code,
  ...toStopIdentity(poi),
});

/**
 * Human-readable labels of everything still blocking a quote/booking — drives
 * the Book Trip disabled hint. Pickup date & time and vehicle live on tickets
 * that haven't landed (DEV-443/444), so they read as missing until then and
 * keep booking gated.
 */
/**
 * Should this address be flagged as needing validation?
 *
 * Two signals, doing two different jobs:
 *
 * `jobVerified` is the AUTHORITATIVE one — `job.job_verified`, the same stored column the operator
 * booking list reports as `metadata.is_verified` and the table shows "Requires Validation" for. It
 * answers *whether this booking needs validating*, so a job the backend considers fine is never
 * flagged here, however odd its coordinates look. That is what stops the sheet and the table
 * disagreeing in front of the same operator.
 *
 * Coordinates answer *which address*, which the job-level flag cannot: a resolved place always has
 * them and an unresolved one never does. They are also what the MAP filters on, for its own reason —
 * Google and Mapbox throw on a non-finite coordinate whatever the job's flag says.
 *
 * `jobVerified` undefined (a fresh draft, or a backend predating the field) falls back to coordinates
 * alone, which is how this behaved before the flag existed.
 *
 * Defined here rather than in selectors.ts because that module imports the store, and this is needed
 * by both.
 */
export const isUnvalidatedLocation = (poi?: PointsOfInterest | null, jobVerified?: boolean): boolean => {
  if (jobVerified === true) return false;

  return !!poi && (!Number.isFinite(poi.lat) || !Number.isFinite(poi.lng));
};

export const getMissingBookingFields = (state: ClientBookingState): string[] => {
  const missing: string[] = [];
  /*
   * Deliberately NOT gated on whether an address was validated. A "Requires Validation" booking has a
   * pickup and a dropoff — they just have no coordinates — and an operator must stay able to edit
   * everything else on it (a passenger name, a vehicle) without first fixing an address they may have
   * no better information about. The unvalidated addresses are flagged in the panel and the summary
   * instead; see isUnvalidatedLocation below.
   */
  if (!state.pickup) missing.push("Pickup location");
  state.stops.forEach((stop, index) => {
    if (!stop) missing.push(`Stop ${index + 1} location`);
  });
  if (!state.destination) missing.push("Dropoff location");
  if (!state.pickupDatetime) missing.push("Pickup date & time");
  if (!state.vehicle) missing.push("Vehicle");
  if (!state.transferType) missing.push("Transfer type");
  // Hourly/daily transfer types can't be priced without a duration (the backend 400s a daily without one).
  if (durationRequired(state) && !state.bookedForDuration) missing.push("Trip duration");
  // Fixed-rate transfer types have no auto-quote — the operator must set the price (legacy parity).
  if (isFixedRate(effectiveRate(state)) && !hasFixedPrice(state)) missing.push("Fixed price");
  if (!state.paymentMethod) missing.push("Payment method");
  return missing;
};

/** The Trip Details sub-steps. Kept in sync with TripDetailItem in TripDetailsPanel.tsx.
 * Declared here (not imported) to avoid a redux→components import cycle. */
type TripDetailItemKey = "passenger" | "datetime" | "vehicle" | "other";

/**
 * Per-sub-step blockers for the Trip Details step, keyed by row. An empty array means that
 * sub-step is complete. Drives the whole-set exit gate (Next Step / Other Details' Proceed) and
 * the danger row states in TripDetailsPanel. Shares its definition of "required" with
 * getMissingBookingFields so the row gate and the submit gate can never disagree.
 *
 * Authed self / favourite passengers are always valid (a default account holder is assumed); only
 * the guest flow, which mints an account from entered contact details, can be missing here.
 */
export const getTripDetailErrors = (state: ClientBookingState, isGuest: boolean): Record<TripDetailItemKey, string[]> => ({
  passenger: isGuest && !state.guestContact ? ["Passenger contact details"] : [],
  datetime: state.pickupDatetime ? [] : ["Pickup date & time"],
  vehicle: [
    ...(state.vehicle ? [] : ["Vehicle"]),
    ...(state.transferType ? [] : ["Transfer type"]),
    ...(durationRequired(state) && !state.bookedForDuration ? ["Trip duration"] : []),
  ],
  // "Booked by" is required in the Other Details panel (and the legacy sheet). It's pre-seeded from
  // the passenger/account name and committed only on Proceed, so it reads as missing until the panel
  // has been visited — the exit gate then routes the user there to confirm it.
  other: state.bookedBy?.trim() ? [] : ["Booked by"],
});

/**
 * The single point where the web draft must match the mobile booking contract
 * (app-customer src/redux/apis/booking/index.ts) — shared by POST /v2/cust/quote
 * and POST /v2/cust/booking. Returns null while the draft is incomplete
 * (see getMissingBookingFields for what's blocking).
 */
export const buildBookingPayload = (state: ClientBookingState): CreateClientBookingPayload | null => {
  const { pickup, destination, pickupDatetime, vehicle, transferType, paymentMethod } = state;
  if (!pickup || !destination || !pickupDatetime || !vehicle || !transferType || !paymentMethod) return null;

  // Fixed-rate transfer types carry no auto-quote; without the operator-entered price the draft can't
  // be quoted or booked, so hold the payload back (mirrors the duration gate for hourly/daily).
  const fixed = isFixedRate(effectiveRate(state));
  if (fixed && !hasFixedPrice(state)) return null;

  const filledStops: PointsOfInterest[] = [];
  for (const stop of state.stops) {
    // An added-but-unfilled stop input means the trip isn't fully specified yet
    if (!stop) return null;
    filledStops.push(stop);
  }

  // Booking for a favourite / new guest → send their contact as vehicles[0].guest; "self" (or none)
  // omits it so the backend books as the account holder.
  const guest = state.passenger && state.passenger.kind !== "self" ? toBookingGuest(state.passenger) : null;
  const extraPassengers = toExtraPassengers(state);

  return {
    vehicles: [
      {
        type: vehicle.vehicle_id,
        passengers: state.numOfPassengers ?? 1,
        bags: state.numOfBags ?? 0,
        extras: toExtraLineItems(state).map((extraId) => ({ "line-item": extraId })),
        ...(guest ? { guest } : {}),
      },
    ],
    stops: [pickup, ...filledStops, destination].map(toBookingStop),
    // The create API rejects bookings without a transfer type ("Invalid transfer type supplied")
    listed_events_list: transferType.id,
    flight_number: state.pickupFlightNumber ?? "",
    flight_not_flying: state.pickupNotFlying,
    // The DEPARTURE flight for an airport drop-off (DEV-1487, contract §2). Sent unconditionally,
    // because `""` is what CLEARS it — exactly how the arrival `flight_number` above is cleared. Omitting
    // the key on a "Not Flying" would leave the previously linked departure attached to the job.
    departure_flight_number: state.departureFlightNumber ?? "",
    // "Delay driver arrival by". The server stores it and recomputes `pickup = flight arrival +
    // delay` whenever the flight moves (DEV-1429), so 0 ("meet me on arrival") must be sent
    // explicitly — an omitted key stores NULL, meaning the passenger was never asked. Only
    // meaningful with a flight, so a non-flight booking omits it.
    ...(state.pickupFlightNumber && state.pickupFlightDelayMinutes != null
      ? { flight_pickup_delay_minutes: state.pickupFlightDelayMinutes }
      : {}),
    // The landing this pickup was derived from (DEV-1503). The server verifies it with exact epoch
    // equality — `landing + delay*60 === pickup`, read in the booking location's timezone — and refuses
    // (and logs) a value that does not reproduce the pickup beside it, so the two are only ever committed
    // together. Absent means "no flight-derived claim" and the server resolves its own arrival, which is
    // every client's behaviour before this ticket. Only meaningful with a flight AND a delay: the check
    // early-returns on a null delay, so a landing without one could never be verified.
    ...(state.pickupFlightNumber && state.pickupFlightDelayMinutes != null && state.pickupFlightLandingUsed
      ? { flight_landing_used: state.pickupFlightLandingUsed }
      : {}),
    booked_for_duration: state.bookedForDuration ?? 0,
    // Only send when set — the backend defaults an omitted booked_by to the logged-in account name,
    // which is the same answer the sheet seeds (see useBookedByPrefill). Sending it explicitly is what
    // carries a value the user EDITED; it records who placed the booking, so under operator/EA OBO
    // that's the operator/assistant, not the client and never the passenger.
    ...(state.bookedBy?.trim() ? { booked_by: state.bookedBy.trim() } : {}),
    // Client reference (Other Details) — only when set; the backend stores "" otherwise.
    ...(state.reference?.trim() ? { reference: state.reference.trim() } : {}),
    // Additional passengers (Passenger 2..N) — persisted to job_passenger; omitted when none.
    ...(extraPassengers.length ? { passengers: extraPassengers } : {}),
    additional_info: state.additionalInfo ?? "",
    ...(hasFixedPrice(state) ? { fixed_price: state.fixedPrice } : {}),
    job_rate: state.jobRate ?? DEFAULT_JOB_RATE,
    // The date panel stores a UTC ISO string (toISOString); the API expects naive local
    // "YYYY-MM-DD HH:mm:ss" (mobile parity), so render back to the picked wall-clock time.
    pickup_datetime: format(new Date(pickupDatetime), "yyyy-MM-dd HH:mm:ss"),
    payment_method_uuid: paymentMethod.uuid,
    booking_device: BOOKING_DEVICE,
    booking_platform: BOOKING_PLATFORM,
  };
};

/** Guest booking `job_rate` default — the guest create contract's journey/transfer value ("fixed"). */
const GUEST_DEFAULT_JOB_RATE = "fixed";

/** Draft POI → the guest booking `stops` shape (no `index`). */
const toGuestStop = (poi: PointsOfInterest): GuestBookingStop => ({
  address: poi.address,
  formatted_address: poi.address,
  lat: poi.lat,
  lng: poi.lng,
  // Drivers see a stop as "{city}, {postcode}"; without it they read "undefined, 2127".
  city: poi.city,
  postcode: poi.postcode,
  state: poi.state,
  state_code: poi.state_code,
  country: poi.country,
  country_code: poi.country_code,
  ...toStopIdentity(poi),
});

/**
 * Build the `booking` sub-object for the guest create endpoint. Mirrors
 * buildBookingPayload but adapts to the guest contract and — crucially — does NOT require a saved
 * `paymentMethod` (the guest pays with a tokenised `pm_`, sent top-level, not inside `booking`).
 * Adaptations vs buildBookingPayload's CreateClientBookingPayload:
 *   - vehicles: `{ type: <numeric>, passengers, guest: {} }` (drops per-vehicle `extras`/`bags`; adds `guest`).
 *   - extras: hoisted to a top-level numeric array `extras: number[]` (was `vehicles[].extras[{ line-item }]`).
 *   - stops: `toGuestStop` shape (address/formatted_address/lat/lng/city/postcode/state/country + the
 *     shared rm_* identity).
 *   - listed_events_list / type coerced to numbers; `job_rate` defaults to "fixed".
 * Returns null while the draft can't produce a valid booking (same completeness rule minus payment).
 */
export const buildGuestBookingObject = (state: ClientBookingState): GuestBookingObject | null => {
  const { pickup, destination, pickupDatetime, vehicle, transferType } = state;
  if (!pickup || !destination || !pickupDatetime || !vehicle || !transferType) return null;

  const filledStops: PointsOfInterest[] = [];
  for (const stop of state.stops) {
    if (!stop) return null;
    filledStops.push(stop);
  }

  return {
    listed_events_list: Number(transferType.id),
    vehicles: [{ type: Number(vehicle.vehicle_id), passengers: state.numOfPassengers ?? 1, guest: {} }],
    stops: [pickup, ...filledStops, destination].map(toGuestStop),
    pickup_datetime: format(new Date(pickupDatetime), "yyyy-MM-dd HH:mm:ss"),
    job_rate: state.jobRate ?? GUEST_DEFAULT_JOB_RATE,
    extras: toExtraLineItems(state)
      .map(Number)
      .filter((id) => !Number.isNaN(id)),
    ...(state.pickupFlightNumber ? { flight_number: state.pickupFlightNumber } : {}),
    flight_not_flying: state.pickupNotFlying,
    // Same contract §2 as the authed payload, and sent for the same reason unconditionally: `""` clears.
    departure_flight_number: state.departureFlightNumber ?? "",
    // Same contract as the authed payload — the guest sheet shows the same delay input (DEV-1429).
    ...(state.pickupFlightNumber && state.pickupFlightDelayMinutes != null
      ? { flight_pickup_delay_minutes: state.pickupFlightDelayMinutes }
      : {}),
    // Same contract again (DEV-1503). The guest resource DROPS an unparseable landing instead of 400ing,
    // so a malformed value here would cost the booking its provenance in silence — one more reason the
    // value is built by `flightLandingUsedIso` rather than assembled at the call site.
    ...(state.pickupFlightNumber && state.pickupFlightDelayMinutes != null && state.pickupFlightLandingUsed
      ? { flight_landing_used: state.pickupFlightLandingUsed }
      : {}),
    ...(state.reference?.trim() ? { reference: state.reference.trim() } : {}),
    ...(toExtraPassengers(state).length ? { passengers: toExtraPassengers(state) } : {}),
    ...(state.additionalInfo ? { additional_info: state.additionalInfo } : {}),
    ...(state.bookedForDuration ? { booked_for_duration: state.bookedForDuration } : {}),
  };
};

const guestQuoteDuration = (state: ClientBookingState): Pick<GuestQuoteBody, "booked_for_duration_hourly" | "booked_for_duration_daily"> => {
  if (!durationRequired(state) || !state.bookedForDuration) return {};
  return transferRateKind(state.transferType?.type) === "daily"
    ? { booked_for_duration_daily: state.bookedForDuration }
    : { booked_for_duration_hourly: state.bookedForDuration };
};

/**
 * Build the POST body for the guest live quote (POST /v2/guest-quote). The endpoint keys the transfer
 * type and each vehicle on their UUIDs and 500s on numeric ids — but the draft only carries the
 * transfer's numeric `id` (VehicleTransferPanel drops the uuid) and may carry the vehicle uuid or only
 * its numeric `vehicle_id`. So both uuids are resolved from the guest job-data (`bookingData`): the
 * transfer by matching `id`, the vehicle by `vehicle_id` (falling back to the draft's own `uuid`).
 * Returns null until the draft is quotable, the provider is known, the job-data has loaded, AND both
 * uuids resolve — the summary skips the query (skipToken) rather than firing a doomed request.
 */
export const buildGuestQuoteBody = (
  state: ClientBookingState,
  providerUuid: string | null | undefined,
  bookingData: ClientBookingData | undefined
): GuestQuoteBody | null => {
  const { pickup, destination, pickupDatetime, vehicle, transferType } = state;
  if (!providerUuid || !pickup || !destination || !pickupDatetime || !vehicle || !transferType || !bookingData) return null;

  const filledStops: PointsOfInterest[] = [];
  for (const stop of state.stops) {
    if (!stop) return null;
    filledStops.push(stop);
  }

  const vehicleUuid = vehicle.uuid ?? bookingData.vehicles.find((v) => v.vehicle_id === vehicle.vehicle_id)?.uuid;
  const transferUuid = bookingData.transfer_types.find((t) => Number(t.id) === Number(transferType.id))?.uuid;
  if (!vehicleUuid || !transferUuid) return null;

  return {
    provider_uuid: providerUuid,
    transfer_type: transferUuid,
    vehicles: [{ type: vehicleUuid }],
    stops: [pickup, ...filledStops, destination].map(toGuestStop),
    extras: toExtraLineItems(state)
      .map(Number)
      .filter((id) => !Number.isNaN(id)),
    pickup_datetime: format(new Date(pickupDatetime), "yyyy-MM-dd HH:mm:ss"),
    ...guestQuoteDuration(state),
    // Only sent once the guest has captured a card — its issuing country lets the backend price the
    // local vs international processing-fee rate. Omitted (not `null`) when absent, so the no-card
    // quote body — and its RTK cache key — is unchanged.
    ...(state.guestPaymentMethodCountry ? { payment_method_country: state.guestPaymentMethodCountry } : {}),
  };
};

/**
 * Stable idempotency key over the quote + built booking object. Same draft → same key, so a retry
 * after a lost success (network drop before the 201 lands) can't double-book. `quoteUuid` is folded
 * in when a guest quote is available; when it's absent the booking hash alone keeps the key stable
 * per draft.
 */
export const buildIdempotencyKey = (quoteUuid: string | undefined, booking: GuestBookingObject): string => {
  const raw = `${quoteUuid ?? ""}:${JSON.stringify(booking)}`;
  // djb2 — a compact, deterministic string hash (no crypto dependency needed for an idempotency token).
  let hash = 5381;
  for (let i = 0; i < raw.length; i += 1) hash = ((hash << 5) + hash + raw.charCodeAt(i)) >>> 0;
  return `guest-${hash.toString(16)}`;
};
